/* ============================================================================
   Light / dark mode.

   Three settings, not two: "system" (the default) follows the OS and keeps
   following it as the OS flips, while "light" and "dark" pin the choice. Only
   an explicit pin is stored - "system" is represented by the ABSENCE of the
   key, so a user who never touches the control simply tracks their machine
   forever and we never have to migrate a stale stored value.

   Everything downstream keys off one attribute: data-theme="dark" on <html>.
   theme.css defines the light palette on :root and overrides it under that
   attribute, so flipping it re-colours the whole portal with no re-render.

   The same attribute is written a second time, earlier, by an inline script in
   public/index.html - before first paint, so a dark user never sees a white
   flash. That script and applyTheme() must agree; if you change the storage
   key or the attribute, change it there too.
   ========================================================================== */
export const THEME_EVENT = "app:theme";
export const THEME_KEY = "theme";

const media = () => window.matchMedia("(prefers-color-scheme: dark)");

/** The user's setting: "system" | "light" | "dark". */
export const getThemeSetting = () => {
  const stored = localStorage.getItem(THEME_KEY);
  return stored === "light" || stored === "dark" ? stored : "system";
};

/** What is actually on screen: "light" | "dark". */
export const getResolvedTheme = () => {
  const setting = getThemeSetting();
  if (setting !== "system") return setting;
  return media().matches ? "dark" : "light";
};

const applyTheme = () => {
  const resolved = getResolvedTheme();
  const root = document.documentElement;
  if (resolved === "dark") {
    root.setAttribute("data-theme", "dark");
  } else {
    root.removeAttribute("data-theme");
  }
  // Charts render SVG with inline styling and cannot read CSS custom
  // properties, so they have to be told. See utility/chartTheme.js.
  window.dispatchEvent(
    new CustomEvent(THEME_EVENT, { detail: { setting: getThemeSetting(), resolved } })
  );
  return resolved;
};

const commitTheme = (setting) => {
  if (setting === "light" || setting === "dark") {
    localStorage.setItem(THEME_KEY, setting);
  } else {
    localStorage.removeItem(THEME_KEY);
  }
  return applyTheme();
};

const resolveSetting = (setting) =>
  setting === "light" || setting === "dark"
    ? setting
    : media().matches
    ? "dark"
    : "light";

let revealSeq = 0;

/**
 * Stores the setting and re-colours the page.
 *
 * Given `origin` (the control that was clicked), the new palette is revealed
 * as a circle growing out of that control's centre until it covers the
 * viewport — a View Transition, so the browser snapshots the old page and
 * clips the live new one over it; the `.theme-vt` rules in theme.css do the
 * drawing. It falls back to the instant swap when there is no origin, no
 * `document.startViewTransition` (Firefox < 144, Safari < 18), the user asked
 * for reduced motion, or the switch does not change what is on screen
 * (System -> Light on a light machine has nothing to reveal).
 */
export const setTheme = (setting, origin) => {
  const animate =
    origin &&
    typeof document.startViewTransition === "function" &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches &&
    resolveSetting(setting) !== getResolvedTheme();
  if (!animate) return commitTheme(setting);

  const rect = origin.getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  // far enough to reach the corner furthest from the origin
  const radius = Math.hypot(
    Math.max(x, window.innerWidth - x),
    Math.max(y, window.innerHeight - y)
  );

  const root = document.documentElement;
  root.style.setProperty("--theme-vt-x", x + "px");
  root.style.setProperty("--theme-vt-y", y + "px");
  root.style.setProperty("--theme-vt-r", Math.ceil(radius) + "px");
  root.setAttribute("data-theme-vt", "");

  // A click mid-reveal starts a new transition and skips this one, whose
  // `finished` then settles early - only the latest may clean up, or it
  // would strip the attribute the running reveal is drawn from.
  const seq = ++revealSeq;
  // the update callback runs after the old page is snapshotted, i.e. later
  const transition = document.startViewTransition(() => commitTheme(setting));
  transition.finished.finally(() => {
    if (seq !== revealSeq) return;
    root.removeAttribute("data-theme-vt");
    root.style.removeProperty("--theme-vt-x");
    root.style.removeProperty("--theme-vt-y");
    root.style.removeProperty("--theme-vt-r");
  });
  return resolveSetting(setting);
};

/**
 * Called once from src/index.js. Reconciles the attribute the inline script
 * set, then keeps it in sync with the OS for as long as the setting is
 * "system". Returns the unsubscribe function.
 */
export const initTheme = () => {
  applyTheme();
  const onSystemChange = () => {
    // A pinned choice outranks the OS; only "system" reacts.
    if (getThemeSetting() === "system") applyTheme();
  };
  const mq = media();
  // Safari < 14 has no addEventListener on MediaQueryList.
  if (mq.addEventListener) {
    mq.addEventListener("change", onSystemChange);
    return () => mq.removeEventListener("change", onSystemChange);
  }
  mq.addListener(onSystemChange);
  return () => mq.removeListener(onSystemChange);
};

/**
 * Runs `run` with the page forced to light, then puts the mode back.
 *
 * @media print cannot help here: react-to-pdf goes through html2canvas, which
 * rasterises the styles that are actually ON SCREEN and ignores print media
 * entirely. A dark-mode user would otherwise download a black payslip and burn
 * a toner cartridge printing it. The extra frame gives the browser a chance to
 * repaint before the capture reads the DOM.
 */
export const withLightAppearance = async (run) => {
  const root = document.documentElement;
  const wasDark = root.getAttribute("data-theme") === "dark";
  if (wasDark) root.removeAttribute("data-theme");
  try {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    return await run();
  } finally {
    if (wasDark) root.setAttribute("data-theme", "dark");
  }
};

export default initTheme;
